import React, { useState, useEffect } from 'react';
import { 
  Users, LayoutDashboard, Settings, LogOut, Plus, Search, 
  TrendingUp, Activity, Calendar, Mic, ChevronRight, Bell,
  Moon, Sun, Pill, User, Mail, Lock, Palette, Globe
} from 'lucide-react';
import { useAuth, coachAPI, clientsAPI } from '../api';
import { useTheme } from '../ThemeContext';

// Vitamin icon component
const VitaminIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <ellipse cx="12" cy="12" rx="10" ry="6" fill="currentColor" opacity="0.3"/>
    <ellipse cx="12" cy="12" rx="8" ry="4.5" fill="currentColor"/>
    <path d="M4 12C4 9.5 7.5 7.5 12 7.5C16.5 7.5 20 9.5 20 12" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
  </svg>
);

// Format vitamins (multiply by 100 for enhanced display)
const formatVitamins = (value) => {
  return (value * 100).toLocaleString();
};

const Dashboard = ({ onSelectClient }) => {
  const { coach, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [clients, setClients] = useState([]);
  const [usage, setUsage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, usageRes] = await Promise.all([
        clientsAPI.list(),
        coachAPI.getUsage(),
      ]);
      setClients(clientsRes.data.clients);
      setUsage(usageRes.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const StatCard = ({ icon: Icon, label, value, trend, color, isVitamin }) => (
    <div className="card-organic">
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl ${color}`}>
          {isVitamin ? (
            <VitaminIcon size={22} className="text-white" />
          ) : (
            <Icon size={22} className="text-white" />
          )}
        </div>
        {trend && (
          <span className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--sage-primary)' }}>
            <TrendingUp size={14} />
            {trend}
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          {isVitamin ? formatVitamins(value) : value}
        </p>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-main)' }}>
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 border-r p-6 z-40" style={{ backgroundColor: 'var(--bg-sidebar)', borderColor: 'var(--border-color)' }}>
        <div className="mb-10 flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-[#6B8E7D] to-[#8AA894]">
            <Pill size={24} className="text-white" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
            Antropia
          </h1>
        </div>

        <nav className="space-y-2">
          <button 
            data-testid="nav-dashboard" 
            onClick={() => setActiveView('dashboard')}
            className={`sidebar-item w-full text-left ${activeView === 'dashboard' ? 'active' : ''}`}
          >
            <LayoutDashboard size={20} />
            Dashboard
          </button>
          <button 
            data-testid="nav-clients" 
            onClick={() => setActiveView('clients')}
            className={`sidebar-item w-full text-left ${activeView === 'clients' ? 'active' : ''}`}
          >
            <Users size={20} />
            Clientes
          </button>
          <button 
            data-testid="nav-calendar" 
            onClick={() => setActiveView('calendar')}
            className={`sidebar-item w-full text-left ${activeView === 'calendar' ? 'active' : ''}`}
          >
            <Calendar size={20} />
            Calendario
          </button>
          <button 
            data-testid="nav-settings" 
            onClick={() => setActiveView('settings')}
            className={`sidebar-item w-full text-left ${activeView === 'settings' ? 'active' : ''}`}
          >
            <Settings size={20} />
            Configuración
          </button>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-between p-3 rounded-xl mb-4 transition-colors"
            style={{ backgroundColor: 'var(--bg-main)' }}
            data-testid="theme-toggle"
          >
            <span className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {isDark ? <Moon size={18} /> : <Sun size={18} />}
              {isDark ? 'Modo oscuro' : 'Modo claro'}
            </span>
            <div className={`theme-toggle ${isDark ? 'active' : ''}`}>
              <div className="theme-toggle-knob" />
            </div>
          </button>

          {/* Coach Info */}
          <div className="p-4 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, rgba(107, 142, 125, 0.15) 0%, rgba(107, 142, 125, 0.05) 100%)' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium" style={{ backgroundColor: 'var(--sage-primary)' }}>
                {coach?.name?.charAt(0) || 'C'}
              </div>
              <div>
                <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{coach?.name}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{coach?.specialty || 'Coach'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="vitamin-badge">
                <VitaminIcon size={16} />
                <span>{formatVitamins(usage?.tokens_remaining || 0)}</span>
              </div>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Vitaminas</span>
            </div>
          </div>
          
          <button 
            data-testid="logout-btn"
            onClick={logout}
            className="sidebar-item w-full text-left hover:text-red-500"
            style={{ color: 'var(--text-muted)' }}
          >
            <LogOut size={20} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {activeView === 'dashboard' && (
          <DashboardView 
            coach={coach}
            usage={usage}
            clients={filteredClients}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            loading={loading}
            onNewClient={() => setShowNewClientModal(true)}
            onSelectClient={onSelectClient}
          />
        )}
        {activeView === 'clients' && (
          <ClientsView
            clients={filteredClients}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            loading={loading}
            onNewClient={() => setShowNewClientModal(true)}
            onSelectClient={onSelectClient}
          />
        )}
        {activeView === 'calendar' && (
          <CalendarView clients={clients} />
        )}
        {activeView === 'settings' && (
          <SettingsView coach={coach} />
        )}
      </main>

      {/* New Client Modal */}
      {showNewClientModal && (
        <NewClientModal 
          onClose={() => setShowNewClientModal(false)}
          onClientCreated={(newClient) => {
            setClients([newClient, ...clients]);
            setShowNewClientModal(false);
            if (usage) {
              setUsage({ ...usage, clients_count: (usage.clients_count || 0) + 1 });
            }
          }}
        />
      )}
    </div>
  );
};

// Dashboard View
const DashboardView = ({ coach, usage, clients, searchQuery, setSearchQuery, loading, onNewClient, onSelectClient }) => {
  const StatCard = ({ icon: Icon, label, value, trend, color, isVitamin }) => (
    <div className="card-organic">
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl ${color}`}>
          {isVitamin ? (
            <VitaminIcon size={22} className="text-white" />
          ) : (
            <Icon size={22} className="text-white" />
          )}
        </div>
        {trend && (
          <span className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--sage-primary)' }}>
            <TrendingUp size={14} />
            {trend}
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          {isVitamin ? formatVitamins(value) : value}
        </p>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
      </div>
    </div>
  );

  return (
    <>
      {/* Header */}
      <header className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
            Buenos días, {coach?.name?.split(' ')[0]}
          </h2>
          <p style={{ color: 'var(--text-muted)' }} className="mt-1">
            {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <button className="p-3 rounded-xl border transition-colors" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
            <Bell size={20} style={{ color: 'var(--text-muted)' }} />
          </button>
          <button 
            data-testid="new-client-btn"
            onClick={onNewClient}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            Nuevo cliente
          </button>
        </div>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          icon={Users} 
          label="Clientes activos" 
          value={usage?.clients_count || 0}
          trend="+12%"
          color="bg-[#6B8E7D]"
        />
        <StatCard 
          icon={Mic} 
          label="Consultas este mes" 
          value={usage?.consultations_this_month || 0}
          color="bg-[#C25934]"
        />
        <StatCard 
          icon={Activity} 
          label="Vitaminas usadas" 
          value={usage?.tokens_used || 0}
          color="bg-stone-700 dark:bg-stone-600"
          isVitamin
        />
        <StatCard 
          icon={TrendingUp} 
          label="Vitaminas disponibles" 
          value={usage?.tokens_remaining || 0}
          color="bg-gradient-to-r from-[#FFB347] to-[#FF6B35]"
          isVitamin
        />
      </div>

      {/* Clients Section */}
      <div className="card-organic">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
            Tus clientes
          </h3>
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              data-testid="search-clients-input"
              type="text"
              placeholder="Buscar cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none w-64 transition-all"
              style={{ 
                backgroundColor: 'var(--bg-main)', 
                borderColor: 'var(--border-color)',
                color: 'var(--text-primary)'
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner" />
          </div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--bg-main)' }}>
              <Users size={28} style={{ color: 'var(--text-muted)' }} />
            </div>
            <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              {searchQuery ? 'No se encontraron clientes' : 'Aún no tienes clientes'}
            </h4>
            <p className="mb-6" style={{ color: 'var(--text-muted)' }}>
              {searchQuery ? 'Intenta con otra búsqueda' : 'Comienza agregando tu primer cliente'}
            </p>
            {!searchQuery && (
              <button onClick={onNewClient} className="btn-primary flex items-center gap-2">
                <Plus size={18} />
                Agregar cliente
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client, index) => (
              <button
                key={client.id}
                data-testid={`client-card-${client.id}`}
                onClick={() => onSelectClient(client)}
                className={`text-left p-5 rounded-xl border transition-all group animate-fade-in stagger-${index % 4 + 1}`}
                style={{ borderColor: 'var(--border-color)' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#6B8E7D] to-[#8AA894] flex items-center justify-center text-white font-medium text-lg">
                    {client.name.charAt(0)}
                  </div>
                  <ChevronRight size={18} className="transition-colors" style={{ color: 'var(--text-muted)' }} />
                </div>
                <h4 className="font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{client.name}</h4>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  {client.gender === 'HOMBRE' ? 'Masculino' : 'Femenino'} • {client.age} años
                </p>
                {client.height_cm && (
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{client.height_cm} cm</p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// Clients View
const ClientsView = ({ clients, searchQuery, setSearchQuery, loading, onNewClient, onSelectClient }) => (
  <>
    <header className="flex items-center justify-between mb-8">
      <div>
        <h2 className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          Clientes
        </h2>
        <p style={{ color: 'var(--text-muted)' }} className="mt-1">
          {clients.length} clientes activos
        </p>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Buscar cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none w-64 transition-all"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          />
        </div>
        <button onClick={onNewClient} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          Nuevo cliente
        </button>
      </div>
    </header>

    <div className="card-organic">
      <table className="w-full">
        <thead>
          <tr style={{ borderColor: 'var(--border-color)' }}>
            <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Cliente</th>
            <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Género</th>
            <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Edad</th>
            <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Altura</th>
            <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client) => (
            <tr key={client.id} className="border-t cursor-pointer transition-colors" style={{ borderColor: 'var(--border-color)' }} onClick={() => onSelectClient(client)}>
              <td className="py-4 px-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6B8E7D] to-[#8AA894] flex items-center justify-center text-white font-medium">
                    {client.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{client.name}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{client.email || 'Sin email'}</p>
                  </div>
                </div>
              </td>
              <td className="py-4 px-4" style={{ color: 'var(--text-secondary)' }}>{client.gender === 'HOMBRE' ? 'Masculino' : 'Femenino'}</td>
              <td className="py-4 px-4" style={{ color: 'var(--text-secondary)' }}>{client.age} años</td>
              <td className="py-4 px-4" style={{ color: 'var(--text-secondary)' }}>{client.height_cm ? `${client.height_cm} cm` : '—'}</td>
              <td className="py-4 px-4">
                <button className="btn-secondary text-sm py-1.5 px-4">Ver perfil</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

// Calendar View
const CalendarView = ({ clients }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  // Mock appointments
  const appointments = {
    5: [{ client: 'Ana Torres', time: '10:00', type: 'Consulta' }],
    12: [{ client: 'Carlos Mendoza', time: '15:00', type: 'Medición' }],
    18: [{ client: 'Laura Ramírez', time: '11:00', type: 'Seguimiento' }],
    25: [{ client: 'Diego Hernández', time: '09:00', type: 'Consulta' }],
  };

  return (
    <>
      <header className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
            Calendario
          </h2>
          <p style={{ color: 'var(--text-muted)' }} className="mt-1">
            Gestiona tus citas y consultas
          </p>
        </div>
        
        <button className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          Nueva cita
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-organic">
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
              className="p-2 rounded-lg transition-colors"
              style={{ color: 'var(--text-secondary)' }}
            >
              ←
            </button>
            <h3 className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button 
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
              className="p-2 rounded-lg transition-colors"
              style={{ color: 'var(--text-secondary)' }}
            >
              →
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {dayNames.map(day => (
              <div key={day} className="text-center py-2 text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>
                {day}
              </div>
            ))}
            {days.map((day, index) => (
              <div
                key={index}
                className={`aspect-square p-1 rounded-lg transition-colors ${
                  day ? 'cursor-pointer' : ''
                } ${day === new Date().getDate() && currentDate.getMonth() === new Date().getMonth() ? 'bg-[#6B8E7D] text-white' : ''}`}
                style={{ 
                  backgroundColor: appointments[day] ? 'rgba(107, 142, 125, 0.1)' : undefined,
                  color: day === new Date().getDate() && currentDate.getMonth() === new Date().getMonth() ? 'white' : 'var(--text-primary)'
                }}
              >
                {day && (
                  <div className="h-full flex flex-col items-center justify-center">
                    <span className="text-sm font-medium">{day}</span>
                    {appointments[day] && (
                      <div className="w-1.5 h-1.5 rounded-full bg-[#C25934] mt-1" />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="card-organic">
          <h3 className="text-lg font-medium mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
            Próximas citas
          </h3>
          <div className="space-y-3">
            {Object.entries(appointments).slice(0, 4).map(([day, appts]) => (
              appts.map((appt, i) => (
                <div key={`${day}-${i}`} className="p-3 rounded-xl border" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium" style={{ color: 'var(--sage-primary)' }}>{appt.type}</span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{day} {monthNames[currentDate.getMonth()].slice(0, 3)}</span>
                  </div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{appt.client}</p>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{appt.time}</p>
                </div>
              ))
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

// Settings View
const SettingsView = ({ coach }) => {
  const { isDark, toggleTheme } = useTheme();
  const [activeSection, setActiveSection] = useState('profile');

  const sections = [
    { id: 'profile', label: 'Perfil', icon: User },
    { id: 'account', label: 'Cuenta', icon: Lock },
    { id: 'appearance', label: 'Apariencia', icon: Palette },
    { id: 'notifications', label: 'Notificaciones', icon: Bell },
  ];

  return (
    <>
      <header className="mb-8">
        <h2 className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          Configuración
        </h2>
        <p style={{ color: 'var(--text-muted)' }} className="mt-1">
          Personaliza tu experiencia en Antropia
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="card-organic p-4">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeSection === section.id ? 'bg-[#6B8E7D]/10' : ''
                }`}
                style={{ color: activeSection === section.id ? 'var(--sage-primary)' : 'var(--text-secondary)' }}
              >
                <section.icon size={20} />
                {section.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="lg:col-span-3 card-organic">
          {activeSection === 'profile' && (
            <div className="space-y-6">
              <h3 className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
                Información del perfil
              </h3>
              
              <div className="flex items-center gap-6 pb-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#6B8E7D] to-[#8AA894] flex items-center justify-center text-white text-2xl font-semibold">
                  {coach?.name?.charAt(0) || 'C'}
                </div>
                <div>
                  <button className="btn-secondary text-sm">Cambiar foto</button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                    Nombre completo
                  </label>
                  <input type="text" defaultValue={coach?.name} className="input-organic" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                    Especialidad
                  </label>
                  <input type="text" defaultValue={coach?.specialty} className="input-organic" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                    Email
                  </label>
                  <input type="email" defaultValue={coach?.email} className="input-organic" />
                </div>
              </div>

              <div className="pt-4">
                <button className="btn-primary">Guardar cambios</button>
              </div>
            </div>
          )}

          {activeSection === 'appearance' && (
            <div className="space-y-6">
              <h3 className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
                Apariencia
              </h3>
              
              <div className="flex items-center justify-between p-4 rounded-xl border" style={{ borderColor: 'var(--border-color)' }}>
                <div className="flex items-center gap-3">
                  {isDark ? <Moon size={24} /> : <Sun size={24} />}
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>Modo oscuro</p>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Cambia entre tema claro y oscuro</p>
                  </div>
                </div>
                <button
                  onClick={toggleTheme}
                  className={`theme-toggle ${isDark ? 'active' : ''}`}
                >
                  <div className="theme-toggle-knob" />
                </button>
              </div>
            </div>
          )}

          {activeSection === 'account' && (
            <div className="space-y-6">
              <h3 className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
                Seguridad de la cuenta
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                    Contraseña actual
                  </label>
                  <input type="password" className="input-organic" placeholder="••••••••" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                    Nueva contraseña
                  </label>
                  <input type="password" className="input-organic" placeholder="••••••••" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                    Confirmar nueva contraseña
                  </label>
                  <input type="password" className="input-organic" placeholder="••••••••" />
                </div>
              </div>

              <div className="pt-4">
                <button className="btn-primary">Cambiar contraseña</button>
              </div>
            </div>
          )}

          {activeSection === 'notifications' && (
            <div className="space-y-6">
              <h3 className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
                Notificaciones
              </h3>
              
              <div className="space-y-4">
                {[
                  { label: 'Nuevas citas', desc: 'Recibe notificaciones cuando se agenda una cita' },
                  { label: 'Recordatorios', desc: 'Recordatorios de citas próximas' },
                  { label: 'Reportes semanales', desc: 'Resumen semanal de actividad' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-xl border" style={{ borderColor: 'var(--border-color)' }}>
                    <div>
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{item.desc}</p>
                    </div>
                    <button className="theme-toggle active">
                      <div className="theme-toggle-knob" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// New Client Modal Component
const NewClientModal = ({ onClose, onClientCreated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    gender: 'HOMBRE',
    age: '',
    height_cm: '',
    notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await clientsAPI.create({
        ...formData,
        age: parseInt(formData.age),
        height_cm: formData.height_cm ? parseFloat(formData.height_cm) : null,
        email: formData.email || null,
      });
      onClientCreated(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear el cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="rounded-2xl w-full max-w-lg p-8 animate-fade-in" style={{ backgroundColor: 'var(--bg-card)' }} data-testid="new-client-modal">
        <h3 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          Nuevo cliente
        </h3>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
              Nombre completo *
            </label>
            <input
              data-testid="client-name-input"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-organic"
              placeholder="Juan Pérez"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                Género *
              </label>
              <select
                data-testid="client-gender-select"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="input-organic"
                required
              >
                <option value="HOMBRE">Masculino</option>
                <option value="MUJER">Femenino</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                Edad *
              </label>
              <input
                data-testid="client-age-input"
                type="number"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                className="input-organic"
                placeholder="30"
                min="1"
                max="120"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                Altura (cm)
              </label>
              <input
                data-testid="client-height-input"
                type="number"
                value={formData.height_cm}
                onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })}
                className="input-organic"
                placeholder="175"
                min="50"
                max="300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                Email
              </label>
              <input
                data-testid="client-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-organic"
                placeholder="cliente@email.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
              Notas
            </label>
            <textarea
              data-testid="client-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input-organic resize-none"
              rows={3}
              placeholder="Objetivos, condiciones especiales, etc."
            />
          </div>

          {error && (
            <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm" data-testid="client-error">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
              data-testid="cancel-client-btn"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
              data-testid="create-client-btn"
            >
              {loading ? <div className="spinner" /> : 'Crear cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Dashboard;
